import * as THREE from 'three'

import MathsUtils from '@/utils/mathsUtils'
import BaseObject from './baseObject'
import GlassDesignerTypes from '../types'


// Icon Object
// A generic icon object in the scene to display warning/error icons. Used by guideline system
export default class IconObject extends BaseObject {
  constructor(editor, options = {}) {
    super(editor)

    this.persistable = false
    this.colour = options.colour || '#000000'
    this.bgColour = options.bgColour || '#FFFFFF'
    this.ringColour = options.ringColour || '#606060'
    this.hoverColour = options.hoverColour || '#808080'
    this.iconTexture = options.iconTexture

    this.shadowOpacity = 0.2
    this.shadowOffset = new THREE.Vector2(2, 2)
    this.iconSize = 32
    this.angle = 0
    this.position = new THREE.Vector2(0, 0)
    this.mouseHover = false

    this.iconGeometry = null
    this.iconObject = null
    this.iconBGGeometry = null
    this.iconBGObject = null
    this.iconOutlineGeometry = null
    this.iconOutlineObject = null

    this.displayOffsetAmount = new THREE.Vector2(20, 20)
    
    this.listenMouseEvents = true

    this.sceneObject = new THREE.Object3D()
    this.updateSceneObject()
  }

  // Dispose
  dispose() {
    super.dispose()

    if(this.iconGeometry) {
      this.iconGeometry.dispose()
      this.iconGeometry = null
    }
    this.iconObject = null

    if(this.iconBGGeometry) {
      this.iconBGGeometry.dispose()
      this.iconBGGeometry = null
    }
    this.iconBGObject = null

    if(this.iconOutlineGeometry) {
      this.iconOutlineGeometry.dispose()
      this.iconOutlineGeometry = null
    }
    this.iconOutlineObject = null
  }

  // onBeforeRender
  // Called prior to rendering of the object
  onBeforeRender() {
    super.onBeforeRender()

    let positionZ = 230
    var iconScale = 1.0 / this.editor.camera.zoom

    let angleRads = MathsUtils.degreesToRadians(this.angle)
    let objAngles = new THREE.Euler(0, 0, angleRads)


    if(this.iconObject) {
      this.iconObject.position.set(this.position.x + this.displayOffsetAmount.x * iconScale, this.position.y + this.displayOffsetAmount.y * iconScale, positionZ)
      this.iconObject.scale.set(-iconScale, -iconScale, 1)     // Flipped to adjust for texture

      this.iconObject.material.color.set(this.mouseHover ? this.hoverColour : this.colour)
      this.iconObject.setRotationFromEuler(objAngles)
    }
    if(this.iconBGObject) {
      this.iconBGObject.position.set(this.position.x + this.displayOffsetAmount.x * iconScale, this.position.y + this.displayOffsetAmount.y * iconScale, positionZ-10)
      this.iconBGObject.scale.set(-iconScale, -iconScale, 1)     // Flipped to adjust for texture

      this.iconBGObject.material.color.set(this.bgColour)
    }
    if(this.iconOutlineObject) {
      this.iconOutlineObject.position.set(this.position.x + this.displayOffsetAmount.x * iconScale, this.position.y + this.displayOffsetAmount.y * iconScale, positionZ-10)
      this.iconOutlineObject.scale.set(-iconScale, -iconScale, 1)     // Flipped to adjust for texture

      this.iconOutlineObject.material.color.set(this.mouseHover ? this.colour : this.ringColour)
      this.iconOutlineObject.setRotationFromEuler(objAngles)
    }
  }


  // Update the position of the icon
  setPosition(pos) {
    this.position.set(pos.x, pos.y)
  }

  // Update the rotation of the icon
  // Set in degrees
  setRotation(angle) {
    this.angle = angle
  }


  /**
   * Build the initial drawing objects to display the editing line
   */
  updateSceneObject() {
    this.clearSceneObject()

    this.iconBGGeometry = new THREE.BoxGeometry(this.iconSize, this.iconSize, 2.5)
    let objectBGMaterial = new THREE.MeshBasicMaterial({
      map: this.editor.resources.iconTextureFill,
      color: this.bgColour,
      transparent: true,
      opacity: 1.0,
      depthWrite: false,
      depthTest: false
    })

    this.iconBGObject = new THREE.Mesh(this.iconBGGeometry, objectBGMaterial)
    this.iconBGObject.scale.x = -1    // Flip around for the texture adjustment
    this.iconBGObject.scale.y = -1
    this.sceneObject.add(this.iconBGObject)

    this.iconGeometry = new THREE.BoxGeometry(this.iconSize, this.iconSize, 2.5)
    let objectMaterial = new THREE.MeshBasicMaterial({
      map: this.iconTexture,
      color: this.colour,
      transparent: true,
      opacity: 1.0,
      depthWrite: false,
      depthTest: false
    })

    this.iconObject = new THREE.Mesh(this.iconGeometry, objectMaterial)
    this.iconObject.scale.x = -1    // Flip around for the texture adjustment
    this.iconObject.scale.y = -1
    this.sceneObject.add(this.iconObject)

    this.iconOutlineGeometry = new THREE.BoxGeometry(this.iconSize, this.iconSize, 5)
    let iconOutlineMaterial = new THREE.MeshBasicMaterial({
      map: this.editor.resources.iconTextureBG,
      color: this.ringColour,
      transparent: true,
      opacity: 1.0,
      depthWrite: false,
      depthTest: false
    })

    this.iconOutlineObject = new THREE.Mesh(this.iconOutlineGeometry, iconOutlineMaterial)
    this.iconOutlineObject.scale.x = -1    // Flip around for the texture adjustment
    this.iconOutlineObject.scale.y = -1
    this.sceneObject.add(this.iconOutlineObject)
  }


  // onMouseDownEvent
  onMouseDownEvent(ev, mouseData, intersectionInfo) {
    super.onMouseDownEvent(ev, mouseData, intersectionInfo)
  }

  // onMouseMoveEvent
  onMouseMoveEvent(ev, mouseData) {
    super.onMouseMoveEvent(ev, mouseData)
    if(this.editor.isSelectEnabled())
      this.editor.setMouseCursorHand()
  }


  // onMouseUpEvent
  onMouseUpEvent(ev, mouseData) {
    super.onMouseUpEvent(ev, mouseData)
  }


  // onMouseEnterEvent
  onMouseEnterEvent(ev, mouseData) {
    super.onMouseEnterEvent(ev, mouseData)

    this.mouseHover = true
    this.editor.flagRenderRequired()
  }

  // onMouseLeaveEvent
  onMouseLeaveEvent(ev, mouseData) {
    super.onMouseLeaveEvent(ev, mouseData)

    this.mouseHover = false
    this.editor.flagRenderRequired()
  }


  /**
   * Check for intersections between the object and a ray
   * Returns an object containing intersection point & object position
   * Returns null if no intersection occurred
   */
  checkRayIntersection(ray, worldRadius) {
    var renderScale = 1.0 / this.editor.camera.zoom
    var radiusTest = Math.max(worldRadius, 0.1) + (this.iconSize * 0.5 * Math.abs(this.iconObject.scale.x))

    // Create a plane for the editing handle
    // Assumes the editing shape is sitting on the plane at origin (Z = 0)
    var editingPlane = new THREE.Plane(new THREE.Vector3(0, 0, 1), 0)
    var rayInt = new THREE.Vector3()
    if(!ray.intersectPlane(editingPlane, rayInt))
      return null

    var ray2DPoint = new THREE.Vector2(rayInt.x, rayInt.y)
    var pos2DPoint = new THREE.Vector2(this.position.x + this.displayOffsetAmount.x * renderScale, this.position.y + this.displayOffsetAmount.y * renderScale)

    let dist = ray2DPoint.distanceTo(pos2DPoint)
    if(dist <= radiusTest) {
      return {
        intersection: pos2DPoint,
        position: ray2DPoint,
        objectId: this.id,
        distance: dist,
        layer: GlassDesignerTypes.EditorUILayers.editing
      }
    }

    return null
  }

}
